import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Microphone, Lightning, CircleNotch, Warning, Code, CheckCircle,
  MapPin, Gauge, SpeakerHigh
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });
const SC = v => v >= 70 ? 'var(--success)' : v >= 40 ? 'var(--warning)' : 'var(--error)';

export default function VoicePage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState('');
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);
  const [genSchema, setGenSchema] = useState(false);
  const [schemas, setSchemas] = useState(null);
  const [triggering, setTriggering] = useState('');

  useEffect(() => { clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSel(r.data[0].id); }); }, []);
  useEffect(() => { if (!sel) return; axios.get(`${API}/voice/audits/${sel}`, auth()).then(r => { if (r.data.length > 0) setAudit(r.data[0]); }); }, [sel]);

  const handleAudit = async () => {
    setRunning(true);
    try { const r = await axios.post(`${API}/voice/audit/${sel}`, {}, auth()); setAudit(r.data); toast.success(`Voice: ${r.data.overall_voice_score}%`); }
    catch { toast.error('Failed'); } finally { setRunning(false); }
  };

  const handleSchema = async () => {
    setGenSchema(true);
    try { const r = await axios.post(`${API}/voice/generate-schema/${sel}?page_title=Homepage`, {}, auth()); setSchemas(r.data.schemas); toast.success('Schemas generated'); }
    catch { toast.error('Failed'); } finally { setGenSchema(false); }
  };

  const trigger = (agent) => { setTriggering(agent); toast.success(`${agent} agent triggered`); setTimeout(() => setTriggering(''), 1200); };

  const sc = audit?.scores || {};
  const serp = audit?.voice_serp_features || {};
  const lm = audit?.live_metrics || {};

  const scoreData = sc ? Object.entries(sc).map(([k, v]) => ({ name: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), score: v })) : [];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="voice-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">Voice Search Agent</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Voice queries are 3-5x longer, question-led, and highly localised</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleAudit} disabled={running} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
              {running ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Microphone size={16} className="mr-2" />}Run Audit
            </Button>
          </div>
        </div>

        {audit ? (<>
          {/* ═══ PRIMARY SCORE + COMPONENTS ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="nx-metric-card rounded-lg text-center py-6">
              <p className="text-[10px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest mb-2">Voice Readiness Score</p>
              <p className="text-6xl font-black font-mono" style={{ color: SC(audit.overall_voice_score) }}>{audit.overall_voice_score}%</p>
              <p className="text-xs text-[var(--text-tertiary)] mt-2">{audit.overall_voice_score >= 70 ? 'Voice-Ready' : audit.overall_voice_score >= 40 ? 'Needs Optimization' : 'Critical — Voice Blocked'}</p>
            </div>
            <div className="lg:col-span-2 nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Sub-Components</h3>
              <div className="space-y-3">
                {[
                  { key: 'faq_howto_schema', label: 'FAQ/HowTo Schema Coverage', badge: null },
                  { key: 'speakable_schema', label: 'Speakable Schema', badge: 'PRIORITY' },
                  { key: 'conversational_content', label: 'Conversational Content Score', badge: 'Target >70%' },
                  { key: 'voice_query_alignment', label: 'Voice Query Alignment', badge: null },
                ].map(c => (
                  <div key={c.key} className="flex items-center gap-3">
                    <span className="text-[10px] text-[var(--text-secondary)] w-40">{c.label}</span>
                    {c.badge && <span className={`text-[8px] px-1.5 py-0.5 rounded font-mono ${c.badge === 'PRIORITY' ? 'bg-[var(--error-bg)] text-[var(--error)]' : 'bg-[var(--accent-light)] text-[var(--accent)]'}`}>{c.badge}</span>}
                    <div className="flex-1 h-3 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${sc[c.key] || 0}%`, background: SC(sc[c.key] || 0) }} />
                    </div>
                    <span className="text-sm font-bold font-mono text-[var(--text-primary)] w-12 text-right">{sc[c.key] || 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ═══ SERP FEATURES ═══ */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'featured_snippets', label: 'Featured Snippets', icon: '🔍' },
              { key: 'position_zero', label: 'Position Zero', icon: '🏆', sublabel: 'P0 = Voice Default Source' },
              { key: 'knowledge_panels', label: 'Knowledge Panels', icon: '📋' },
              { key: 'people_also_ask', label: 'People Also Ask', icon: '❓' },
            ].map(f => (
              <div key={f.key} className="nx-metric-card rounded-lg">
                <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">{f.label}</span>
                <p className="text-3xl font-black text-[var(--text-primary)] font-mono mt-1">{serp[f.key] || 0}</p>
                {f.sublabel && <p className="text-[8px] text-[var(--warning)] font-mono mt-0.5">{f.sublabel}</p>}
              </div>
            ))}
          </div>

          {/* ═══ INTELLIGENCE TABLE ═══ */}
          {audit.intelligence?.length > 0 && (
            <div className="nx-metric-card rounded-lg">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">
                <Lightning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />Proactive Actions
              </h3>
              <table className="w-full border-collapse">
                <thead><tr className="border-b border-[var(--border)]"><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[25%]">Signal</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[40%]">Intelligence</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[35%]">Action</th></tr></thead>
                <tbody>
                  {audit.intelligence.map(r => (
                    <tr key={r.id} className="border-b border-[var(--border-light)]">
                      <td className="p-2"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: r.severity === 'high' ? 'var(--error)' : 'var(--warning)' }} /><span className="text-xs text-[var(--text-primary)]">{r.indicator}</span></span></td>
                      <td className="p-2 text-xs text-[var(--text-secondary)]">{r.intelligence}</td>
                      <td className="p-2"><p className="text-[10px] text-[var(--text-secondary)] mb-1">{r.action}</p>
                        <Button size="sm" onClick={() => r.agent === 'voice' ? handleSchema() : trigger(r.agent)} disabled={triggering === r.agent || genSchema}
                          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7 px-3">
                          <Lightning size={10} className="mr-1" />{r.agent === 'voice' ? 'Generate Schemas' : 'Execute'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Generated Schemas */}
          {schemas && (
            <div className="nx-metric-card rounded-lg animate-fade-in-up">
              <h3 className="text-sm font-bold text-[var(--text-primary)] mb-3"><Code size={16} className="inline mr-2 text-[var(--accent)]" /> Generated Voice Schemas (JSON-LD)</h3>
              <pre className="text-xs text-[var(--text-secondary)] bg-black/30 p-4 rounded overflow-x-auto max-h-48 font-mono">{typeof schemas === 'string' ? schemas : JSON.stringify(schemas, null, 2)}</pre>
            </div>
          )}

          {/* ═══ 4 LIVE METRICS ═══ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* M1: Vernacular Voice */}
            {lm.vernacular_voice && (
              <div className="nx-metric-card rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[var(--text-primary)]">Vernacular Voice Index</span>
                  <span className="text-[9px] text-[var(--text-tertiary)] font-mono">Target: {lm.vernacular_voice.target}%</span>
                </div>
                {lm.vernacular_voice.auto_brief_langs?.length > 0 && (
                  <p className="text-[9px] bg-[var(--error-bg)] text-[var(--error)] p-1.5 rounded mb-2 font-mono">Auto-briefed: {lm.vernacular_voice.auto_brief_langs.join(', ')} (below 40%)</p>
                )}
                <div className="space-y-2">
                  {Object.entries(lm.vernacular_voice.languages || {}).map(([lang, d]) => (
                    <div key={lang} className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--text-secondary)] w-24 capitalize">{lang.replace('_', ' ')}</span>
                      <div className="flex-1 h-2.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${d.score}%`, background: SC(d.score) }} />
                      </div>
                      <span className="text-[10px] font-mono text-[var(--text-primary)] w-8 text-right">{d.score}%</span>
                      <div className="flex gap-1">
                        {d.speakable_present ? <CheckCircle size={10} className="text-[var(--success)]" weight="fill" /> : <Warning size={10} className="text-[var(--error)]" weight="fill" />}
                        <span className="text-[8px] text-[var(--text-tertiary)]">{d.script}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* M2: Phonetic Accuracy */}
            {lm.phonetic_accuracy && (
              <div className="nx-metric-card rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[var(--text-primary)]"><SpeakerHigh size={14} className="inline mr-1 text-[var(--accent)]" weight="duotone" />Phonetic Brand Accuracy</span>
                  {lm.phonetic_accuracy.correction_needed && <span className="text-[9px] bg-[var(--warning-bg)] text-[var(--warning)] px-2 py-0.5 rounded font-mono">Pronunciation Fix Needed</span>}
                </div>
                <table className="w-full text-[10px] font-mono">
                  <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-1">Assistant</th><th className="text-right p-1">English</th><th className="text-right p-1">Hindi</th><th className="text-right p-1">Arabic</th></tr></thead>
                  <tbody>
                    {Object.entries(lm.phonetic_accuracy.assistants || {}).map(([asst, langs]) => (
                      <tr key={asst} className="border-b border-[var(--border-light)]">
                        <td className="p-1 text-[var(--text-secondary)]">{asst}</td>
                        {['English', 'Hindi', 'Arabic'].map(l => (
                          <td key={l} className="p-1 text-right" style={{ color: (langs[l] || 0) >= 90 ? 'var(--success)' : (langs[l] || 0) >= 80 ? 'var(--warning)' : 'var(--error)' }}>{langs[l] || 0}%</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {lm.phonetic_accuracy.mispronounced?.length > 0 && (
                  <p className="text-[8px] text-[var(--warning)] font-mono mt-2">Mispronounced: {lm.phonetic_accuracy.mispronounced.join(', ')}</p>
                )}
              </div>
            )}

            {/* M3: Voice-to-Store */}
            {lm.voice_to_store && (
              <div className="nx-metric-card rounded-lg">
                <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block"><MapPin size={14} className="inline mr-1 text-[var(--accent)]" weight="duotone" />Voice-to-Store (Local Intent)</span>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[{ l: 'NAP Score', v: lm.voice_to_store.nap_score }, { l: 'GBP Complete', v: lm.voice_to_store.gbp_completeness }, { l: 'Local Schema', v: lm.voice_to_store.local_schema }].map(m => (
                    <div key={m.l} className="text-center p-2 rounded border border-[var(--border-light)]">
                      <p className="text-lg font-bold font-mono" style={{ color: SC(m.v) }}>{m.v}%</p>
                      <p className="text-[8px] text-[var(--text-tertiary)]">{m.l}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[8px] text-[var(--text-tertiary)] font-mono uppercase mb-1">India Cities</p>
                    {Object.entries(lm.voice_to_store.india_cities || {}).slice(0, 5).map(([city, d]) => (
                      <div key={city} className="flex items-center gap-1 text-[9px] font-mono mb-0.5">
                        <span className="text-[var(--text-secondary)] w-16">{city}</span>
                        <span className={`${d.tier === 'Tier 2' ? 'text-[var(--warning)]' : 'text-[var(--text-primary)]'}`}>{d.score}%</span>
                        <span className="text-[7px] text-[var(--text-tertiary)]">{d.tier}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-[8px] text-[var(--text-tertiary)] font-mono uppercase mb-1">GCC Cities</p>
                    {Object.entries(lm.voice_to_store.gcc_cities || {}).map(([city, d]) => (
                      <div key={city} className="flex items-center gap-1 text-[9px] font-mono mb-0.5">
                        <span className="text-[var(--text-secondary)] w-16">{city}</span>
                        <span className="text-[var(--text-primary)]">{d.score}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* M4: Page Speed / CWV */}
            {lm.page_speed && (
              <div className="nx-metric-card rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[var(--text-primary)]"><Gauge size={14} className="inline mr-1 text-[var(--accent)]" weight="duotone" />Page Speed (Voice Disqualifier)</span>
                  <span className={`text-[9px] font-mono ${lm.page_speed.disqualified_count > 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{lm.page_speed.disqualified_count}/{lm.page_speed.total_pages} disqualified</span>
                </div>
                <table className="w-full text-[9px] font-mono">
                  <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-1">Page</th><th className="text-right p-1">LCP</th><th className="text-right p-1">FID</th><th className="text-right p-1">CLS</th><th className="text-left p-1">Voice</th><th className="p-1"></th></tr></thead>
                  <tbody>
                    {lm.page_speed.pages?.map(p => (
                      <tr key={p.page} className={`border-b border-[var(--border-light)] ${!p.voice_eligible ? 'bg-[var(--error-bg)]' : ''}`}>
                        <td className="p-1 text-[var(--text-primary)]">{p.page}</td>
                        <td className={`p-1 text-right ${p.lcp_status === 'pass' ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{p.lcp}s</td>
                        <td className={`p-1 text-right ${p.fid_status === 'pass' ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{p.fid}ms</td>
                        <td className={`p-1 text-right ${p.cls_status === 'pass' ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{p.cls}</td>
                        <td className="p-1">{p.voice_eligible ? <CheckCircle size={10} className="text-[var(--success)]" weight="fill" /> : <Warning size={10} className="text-[var(--error)]" weight="fill" />}</td>
                        <td className="p-1">{!p.voice_eligible && (
                          <button onClick={() => toast.success(`Website Agent: fixing ${p.page}`)} className="text-[8px] text-[var(--accent)] hover:underline">Fix Now</button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[8px] text-[var(--text-tertiary)] mt-1">Target: LCP &lt;1.8s for voice eligibility</p>
              </div>
            )}
          </div>
        </>) : (
          <div className="nx-metric-card rounded-lg text-center py-16">
            <Microphone size={48} className="mx-auto mb-4 text-[var(--text-tertiary)]" />
            <p className="text-[var(--text-secondary)]">Click "Run Audit" to analyze voice search readiness</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
